/**
 * accessControl.js
 * Estrutura do menu lateral como árvore de recursos + ações, e a função `can`
 * que decide o que cada usuário pode fazer (tabela user_permissions).
 * Regra: sem linha salva = padrão do cargo do usuário naquela loja (ver defaultEntry).
 */
import { normalizeRole } from './roleRules';

export const ACTIONS = [
  { key: 'view', label: 'Visualizar', hint: 'Ver a tela e os dados dela' },
  { key: 'interact', label: 'Interagir', hint: 'Assumir, comentar e responder' },
  { key: 'create', label: 'Criar', hint: 'Cadastrar novos registros' },
  { key: 'edit', label: 'Editar', hint: 'Alterar registros existentes' },
  { key: 'delete', label: 'Excluir', hint: 'Apagar registros' }
];

// Espelha o LeftDrawer. Nós com `children` são grupos (só organizam).
export const ACCESS_TREE = [
  {
    id: 'lojas-grupo', label: 'Lojas', children: [
      { id: 'lojas', label: 'Minhas Lojas', actions: ['view', 'interact'] }
    ]
  },
  {
    id: 'maquinas-grupo', label: 'Máquinas', children: [
      { id: 'maquinas', label: 'Minhas Máquinas', actions: ['view', 'interact', 'create', 'edit', 'delete'] }
    ]
  },
  {
    id: 'pecas', label: 'Peças & Componentes', children: [
      { id: 'pecas-estoque', label: 'Estoque Geral', actions: ['view', 'create', 'edit', 'delete'] },
      { id: 'pecas-movimentacoes', label: 'Movimentações', actions: ['view', 'create', 'edit', 'delete'] }
    ]
  },
  {
    id: 'chamados', label: 'Central de Chamados', children: [
      { id: 'chamados-abertos', label: 'Chamados Abertos', actions: ['view', 'interact', 'create', 'edit', 'delete'] },
      { id: 'chamados-historico', label: 'Histórico', actions: ['view'] }
    ]
  },
  {
    id: 'financeiro', label: 'Financeiro', children: [
      { id: 'financeiro-faturamento', label: 'Faturamento das Máquinas', actions: ['view', 'edit'] },
      { id: 'financeiro-custos', label: 'Custos & Despesas', actions: ['view', 'create', 'edit', 'delete'] }
    ]
  },
  {
    id: 'usuarios-grupo', label: 'Usuários', children: [
      {
        // Cadeia: visualizar > editar permissões > editar cargos (cada etapa exige a anterior)
        id: 'usuarios', label: 'Usuários', actions: ['view', 'interact', 'edit'],
        labels: {
          interact: { label: 'Editar permissões', hint: 'Ajustar o que o usuário vê e faz em cada loja' },
          edit: { label: 'Editar cargos', hint: 'Alterar o cargo do usuário, respeitando a hierarquia' }
        }
      },
      { id: 'registro-sistema', label: 'Registro do sistema', actions: ['view'] }
    ]
  }
];

export const LEAF_RESOURCES = ACCESS_TREE.flatMap((n) => (n.children ? n.children : [n]));

const RESOURCE_BY_ID = Object.fromEntries(LEAF_RESOURCES.map((r) => [r.id, r]));
export const actionsOf = (resourceId) => RESOURCE_BY_ID[resourceId]?.actions || ['view'];

/** Rótulo e dica de uma ação; um recurso pode renomear a ação (ex.: Usuários > Editar cargos). */
export const actionMetaOf = (resourceId, actionKey) => ({
  ...ACTIONS.find((a) => a.key === actionKey),
  ...(RESOURCE_BY_ID[resourceId]?.labels?.[actionKey] || {})
});

// Ações que só valem depois de outra liberada (ordem = etapas da cadeia)
const ACTION_CHAIN = { usuarios: ['view', 'interact', 'edit'] };

/** Ao liberar/bloquear uma ação da cadeia, ajusta as anteriores/posteriores dela. */
export function withChainDependencies(resourceId, entry, key) {
  const chain = ACTION_CHAIN[resourceId];
  if (!chain || !chain.includes(key)) return entry;
  const i = chain.indexOf(key);
  const next = { ...entry };
  if (next[key]) chain.slice(0, i).forEach((k) => { next[k] = true; });
  else chain.slice(i + 1).forEach((k) => { next[k] = false; });
  return next;
}

/** Aba do dashboard (activeDashboardTab) -> recurso controlado. */
export function resourceForTab(tab) {
  if (!tab || tab === 'geral') return 'lojas';
  if (tab === 'maquinas') return 'maquinas';
  if (tab === 'pecas-movimentacoes') return 'pecas-movimentacoes';
  if (tab.startsWith('pecas')) return 'pecas-estoque';
  if (tab === 'chamados-historico') return 'chamados-historico';
  if (tab.startsWith('chamados')) return 'chamados-abertos';
  if (tab === 'financeiro-custos') return 'financeiro-custos';
  if (tab.startsWith('financeiro')) return 'financeiro-faturamento';
  if (tab === 'usuarios') return 'usuarios';
  return 'lojas'; // id de uma loja
}

/* ── Permissões padrão por cargo ─────────────────────────────────────
 * Valem quando não há ajuste salvo em user_permissions para aquela loja/tela.
 *  - Operador: só Central de Chamados
 *  - Técnicos: tudo, menos Financeiro; não alteram cargos
 *  - Gerente: tudo, menos Financeiro
 *  - Proprietário (e sem cargo): tudo
 */
const FINANCEIRO = ['financeiro-faturamento', 'financeiro-custos'];
const CHAMADOS = ['chamados-abertos', 'chamados-historico'];
const allowAll = () => Object.fromEntries(ACTIONS.map((a) => [a.key, true]));
const denyAll = () => Object.fromEntries(ACTIONS.map((a) => [a.key, false]));

// Registro do sistema: de padrão só Proprietário e Gerente (o restante precisa ser liberado)
const REGISTRO_DEFAULT_ROLES = ['proprietario', 'owner', 'gestor'];

export function defaultEntry(role, resourceId) {
  const r = normalizeRole(role);
  if (resourceId === 'registro-sistema') return REGISTRO_DEFAULT_ROLES.includes(r) ? allowAll() : denyAll();
  if (r === 'operador') return CHAMADOS.includes(resourceId) ? allowAll() : denyAll();
  if (r.startsWith('tecnico_')) {
    if (FINANCEIRO.includes(resourceId)) return denyAll();
    if (resourceId === 'usuarios') return { ...allowAll(), edit: false };
    return allowAll();
  }
  if (r === 'gestor') return FINANCEIRO.includes(resourceId) ? denyAll() : allowAll();
  return allowAll();
}

export const rowToEntry = (row) => Object.fromEntries(ACTIONS.map((a) => [a.key, row?.[`can_${a.key}`] !== false]));

export const entryToRow = (entry) => Object.fromEntries(ACTIONS.map((a) => [`can_${a.key}`, entry[a.key] !== false]));

/** O ajuste é igual ao padrão do cargo? (então não precisa de linha salva) */
export const isDefaultEntry = (entry, role, resourceId) => {
  const def = defaultEntry(role, resourceId);
  return ACTIONS.every((a) => (entry[a.key] !== false) === def[a.key]);
};

/**
 * Cria `can(resource, action, locationId?)` a partir das linhas do próprio usuário
 * e do cargo dele em cada loja (lojas: [{ id, role }]).
 * Sem locationId vale "em ao menos uma das minhas lojas".
 * Enquanto lojas/restrições não chegaram (ready = false) tudo é negado: nada aparece antes do bloqueio.
 */
// Modo demo: só visualiza; Usuários mostra apenas a própria pessoa, o registro do sistema fica oculto
const DEMO_HIDDEN = ['registro-sistema'];

export function buildCan(rows, lojas, superAdmin, ready = true, demo = false) {
  const byKey = {};
  // usar_padrao = true: ajuste desfeito, a linha só fica registrada e vale o padrão do cargo
  (rows || []).forEach((r) => { if (!r.usar_padrao) byKey[`${r.location_id}|${r.resource}`] = r; });
  const roleById = Object.fromEntries((lojas || []).map((l) => [String(l.id), l.role]));
  const stores = Object.keys(roleById);

  return (resource, action = 'view', locationId) => {
    if (!ready) return false;
    if (superAdmin) return true;
    if (demo) return action === 'view' && !DEMO_HIDDEN.includes(resource);
    const ids = locationId ? [String(locationId)] : stores;
    if (ids.length === 0) return true;
    return ids.some((id) => {
      const row = byKey[`${id}|${resource}`];
      const entry = row ? rowToEntry(row) : defaultEntry(roleById[id], resource);
      if (action !== 'view' && entry.view === false) return false;
      return entry[action] !== false;
    });
  };
}
